import { useContext, useEffect, useRef, useState } from "react";
import { FaTrash } from "react-icons/fa";
import { io } from "socket.io-client";
import { GlobalContext } from "../../utils/GlobalContext";
import "../Assets/css/index.css";
import axiosMain from "../http/axiosMain";

const initialNotifications = [
  {
    id: 1,
    icon: "₹",
    message: "Payment of INR 5,000 received from Puma on 23rd Dec 2023",
    timestamp: "Wed, 1:20 PM",
    type: "payment",
    read: false,
    status: "unread", // Add status here
  },
  {
    id: 2,
    icon: "📢",
    message: "Puma Posted a New campaign: Republic Day",
    timestamp: "Tue, 1:20 PM",
    type: "campaign",
    read: false,
    status: "unread", // Add status here
  },
  // other initial notifications...
];

const SOCKET_SERVER_URL = "http://localhost:8080"; // Replace with your actual socket server URL

function Notifications({ onClose }) {
  const [notifications, setNotifications] = useState(initialNotifications);
  const [filter, setFilter] = useState("all");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [showNotification, setShowNotification] = useState(true);
  const { userData } = useContext(GlobalContext);

  const socket = useRef(null);

  const fetchNotifications = async () => {
    try {
      const response = await axiosMain.get("/api/notifications/notifications");
      setNotifications(response.data.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    socket.current = io(SOCKET_SERVER_URL);

    socket.current.on("new_notification", (newNotification) => {
      setNotifications((prevNotifications) => [
        newNotification,
        ...prevNotifications,
      ]);
    });

    return () => {
      socket.current.disconnect();
    };
  }, []);

  const markAsRead = async (id) => {
    try {
      await axiosMain.post("/api/notifications/read", {
        "notifications-id": [id],
      });

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === id
            ? { ...notification, read: true, status: "read" }
            : notification
        )
      );

      // Re-apply filter to trigger UI update
      setFilter((prev) => (prev === "unread" ? "unread" : prev));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markMultipleAsRead = async () => {
    if (selectedNotifications.length === 0) return;
    try {
      await axiosMain.put("/api/notifications/notifications", {
        notification_ids: selectedNotifications,
      });

      setNotifications((prev) =>
        prev.map((notification) =>
          selectedNotifications.includes(notification.id)
            ? { ...notification, read: true, status: "read" }
            : notification
        )
      );

      setSelectedNotifications([]);

      // Re-apply filter to update tabs
      setFilter((prev) => (prev === "unread" ? "unread" : prev));
    } catch (error) {
      console.error("Error marking multiple notifications as read:", error);
    }
  };

  const singleRead = async (id) => {
    try {
      await axiosMain.put("/api/notifications/notifications", {
        notification_ids: [id],
      });

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === id
            ? { ...notification, read: true, status: "read" }
            : notification
        )
      );

      // Re-apply filter to update tabs
      setFilter((prev) => (prev === "unread" ? "unread" : prev));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleDelete = async () => {
    if (selectedNotifications.length === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedNotifications.length} notifications?`
    );
    if (confirmDelete) {
      try {
        await axiosMain.delete("/api/notifications/notifications", {
          data: { notification_ids: selectedNotifications },
        });

        setNotifications((prev) =>
          prev.filter(
            (notification) => !selectedNotifications.includes(notification.id)
          )
        );

        setSelectedNotifications([]);
      } catch (error) {
        console.error("Error deleting notifications:", error);
      }
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const filteredNotifications = notifications?.filter((notification) => {
    if (filter === "all") return true;
    if (filter === "unread") return notification.status === "unread";
    if (filter === "read") return notification.status === "read";
    return true;
  });

  const handleSelect = (id) => {
    setSelectedNotifications((prev) =>
      prev.includes(id) ? prev.filter((nid) => nid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications?.map((n) => n.id));
    }
  };

  if (!showNotification) return null;

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <h2>Notifications</h2>
        <button
          className="close-button"
          onClick={onClose}
          style={{ border: "none" }}
        >
          ×
        </button>
      </div>

      <div className="actions-container">
        <div className="notifications-filters">
          {["all", "unread", "read"].map((type) => (
            <button
              key={type}
              className={`filter-btn ${filter === type ? "active" : ""}`}
              onClick={() => setFilter(type)}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        {filteredNotifications.length > 0 && (
          <button
            className="select-button"
            onClick={() => setSelectMode(!selectMode)}
          >
            {selectMode ? "Cancel" : "Select"}
          </button>
        )}
      </div>

      {selectMode && filteredNotifications.length > 0 && (
        <div className="selection-bar">
          <div className="checkbox-container">
            <input
              id="squarecheckbox"
              type="checkbox"
              checked={
                selectedNotifications.length === filteredNotifications.length
              }
              onChange={handleSelectAll}
            />
            <label>Select All</label>
          </div>
          {selectedNotifications.length > 0 && (
            <div style={{display:"flex",gap:"10px"}}>
              <button className="delete-button" onClick={handleDelete}>
                <FaTrash /> Delete
              </button>
              <button style={{border:"1px solid #6B8E76",paddingLeft:"4px",paddingRight:"4px",borderRadius:"8px",fontSize:"14px"}} onClick={markMultipleAsRead}>
                Mark as Read
              </button>
            </div>
          )}
        </div>
      )}

      <div className="notifications-list">
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-item ${
                notification.status === "read" ? "read" : "unread"
              }`}
              onClick={() => singleRead(notification.id)}
              style={{
                backgroundColor:
                  notification.status === "read" ? "#ffffff" : "#f0f8ff",
                fontWeight: notification.status === "read" ? "normal" : "bold",
                padding: "10px",
                transition: "background 0.3s ease",
                cursor: "pointer",
              }}
            >
              {selectMode && (
                <input
                  id="squarecheckbox"
                  type="checkbox"
                  checked={selectedNotifications.includes(notification.id)}
                  onChange={() => handleSelect(notification.id)}
                />
              )}
              <div className={`notification-icon ${notification.type}`}>
                {notification.icon || "₹"}
              </div>
              <div className="notification-content">
                <p>{notification.message}</p>
                <span className="notification-time">
                  {notification.timestamp}
                </span>
                <span className="notification-status">
                  {notification.status}
                </span>{" "}
              </div>
            </div>
          ))
        ) : (
          <div>No notifications available</div>
        )}
      </div>
    </div>
  );
}

export default Notifications;
